import React from "react";
import { Icon } from "../components/Icon";
import { Num } from "../components/Num";
import { maskEmail, maskPhone } from "../utils";
import {
  displayItemNumber,
  groupItemsBySection,
  orderSelectedIds
} from "../pickerGrouping";
import type { AgendaItem, AgendaSection, FormData, Stance } from "../types";

export interface ReviewProps {
  form: FormData;
  selections: Record<number, Stance>;
  agendaItems: AgendaItem[];
  sections: AgendaSection[];
  returningUser: boolean;
  collectDisposition: boolean;
  hasPickStep: boolean;
  onEditPicks: () => void;
  onEditDetails: () => void;
}

export const Review: React.FC<ReviewProps> = ({
  form,
  selections,
  agendaItems,
  sections,
  returningUser,
  collectDisposition,
  hasPickStep,
  onEditPicks,
  onEditDetails
}) => {
  const ids = orderSelectedIds(selections, agendaItems, sections);
  const selectedItems = ids
    .map((id) => agendaItems.find((item) => item.id === id))
    .filter((item): item is AgendaItem => item !== undefined);
  const selectedGroups = groupItemsBySection(selectedItems, sections);
  const counts = { for: 0, against: 0, neutral: 0 };
  ids.forEach((id) => {
    counts[selections[id]]++;
  });

  return (
    <div className="review">
      <div className="main">
        {returningUser ? (
          <>
            <div className="returning-banner">
              <div className="dot"></div>
              <div>
                <strong>
                  Welcome back,{" "}
                  {/* AB#9443: name is PII — notranslate keeps it out of Google
                      Translate. AB#9707: the terminal period lives inside the
                      same island so Google can't shuffle it ahead of the name
                      (see the Done screen h1 for the full rationale). */}
                  <span className="notranslate">{form.first || "friend"}.</span>
                </strong>
                <span>
                  {" "}
                  We found your registration. Confirm or edit anything
                  that&rsquo;s changed.
                </span>
              </div>
            </div>
            <h2>Confirm your check-in</h2>
            <p className="lede">
              Review your details and selections, then tap{" "}
              <strong>Confirm</strong>. Use <strong>Back</strong> to edit.
            </p>
          </>
        ) : (
          <>
            <h2>Review and confirm</h2>
            <p className="lede">
              {hasPickStep ? (
                <>
                  Review your information and the items you&rsquo;ve signed up
                  to speak on, then tap <strong>Confirm</strong>.
                </>
              ) : (
                <>
                  Review your information, then tap <strong>Confirm</strong>.
                </>
              )}
            </p>
          </>
        )}

        <div className="who-head">
          <h3>Your details</h3>
          <button
            type="button"
            className="btn edit-step-btn"
            onClick={onEditDetails}
          >
            <Icon name="pencil" size={18} /> Edit my details
          </button>
        </div>

        <div className="who">
          <div>
            <div className="field-label">Name</div>
            <div className="field-val notranslate">
              {form.first || "—"} {form.last}
            </div>
          </div>
          <div>
            <div className="field-label">Email</div>
            <div className="field-val masked-row">
              <span
                className="masked-val notranslate"
                data-testid="review-email"
              >
                {form.email ? maskEmail(form.email) : "—"}
              </span>
            </div>
          </div>
          <div>
            <div className="field-label">Phone</div>
            <div className="field-val masked-row">
              <span
                className="masked-val notranslate"
                data-testid="review-phone"
              >
                {form.phone ? maskPhone(form.phone) : "—"}
              </span>
            </div>
          </div>
        </div>

        {collectDisposition && ids.length > 0 && (
          <div className="stats">
            <div className="stat for">
              <div className="v">
                {counts.for}
                <span className="of"> / {ids.length}</span>
              </div>
              <div className="l">In favor</div>
            </div>
            <div className="stat against">
              <div className="v">
                {counts.against}
                <span className="of"> / {ids.length}</span>
              </div>
              <div className="l">Opposed</div>
            </div>
            <div className="stat neutral">
              <div className="v">
                {counts.neutral}
                <span className="of"> / {ids.length}</span>
              </div>
              <div className="l">Neutral</div>
            </div>
          </div>
        )}

        {(hasPickStep || ids.length > 0) && (
          <div className="selections">
            <div className="selections-head">
              <h3>Items you'll speak on ({ids.length})</h3>
              {hasPickStep && (
                <button
                  type="button"
                  className="btn edit-step-btn"
                  onClick={onEditPicks}
                >
                  <Icon name="pencil" size={18} />
                  {ids.length === 0 ? "Add items" : "Add or change items"}
                </button>
              )}
            </div>
            {ids.length === 0 && hasPickStep && (
              <div className="empty-state review-empty">
                <p className="big">No items selected</p>
                <p>
                  You can check in to attend without speaking, or pick items
                  now.
                </p>
                <button
                  type="button"
                  className="btn lg primary"
                  onClick={onEditPicks}
                >
                  <Icon name="plus" size={20} /> Choose items to speak on
                </button>
              </div>
            )}
            {selectedGroups.map((group) => (
              <div
                key={group.key}
                role={group.label ? "group" : undefined}
                aria-labelledby={
                  group.label ? `review-${group.key}-head` : undefined
                }
              >
                {group.label && (
                  <div
                    className="group-head"
                    id={`review-${group.key}-head`}
                    role="heading"
                    aria-level={2}
                  >
                    {group.label}
                  </div>
                )}
                {group.items.map((it) => {
                  const s = selections[it.id];
                  const label =
                    s === "for"
                      ? "In favor"
                      : s === "against"
                      ? "Opposed"
                      : "Neutral";
                  return (
                    <div className="row" key={it.id}>
                      <div className="num">
                        <Num>{displayItemNumber(it.itemNumber, it.id)}</Num>
                      </div>
                      <div className="title">{it.title || "Untitled item"}</div>
                      {collectDisposition && (
                        <div className={`badge ${s}`}>{label}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <p className="review-tail">
          Tap <strong>Confirm check-in</strong> below when ready.
        </p>
      </div>
    </div>
  );
};
